import React, { useRef, useState } from "react";
import UploadField from "../../components/UploadField";
import type { AdminNote } from "../../types/types";
import LightCustomButton from "../../components/buttons/LightCustomButton";
import Folder from "../../assets/folder.svg";
import Edit from "../../assets/edit.svg";
import Delete from "../../assets/delete.svg";

/* ----------------------------- Helpers ----------------------------- */


const PlusIcon = () => (
    <div className="h-7 w-7 rounded-lg border border-sky-200 bg-white flex items-center justify-center text-lg">
        +
    </div>
);
const AttachmentIcon: React.FC<{
    url: string;
    onRemove: () => void;
}> = ({ url, onRemove }) => {
    return (
        <div
            className="group flex items-center cursor-pointer
                 rounded-xl border border-sky-200 bg-white
                 h-9 transition-all duration-200
                 hover:border-blue-500"
        >
            {/* Folder (always visible) */}
            <div className="w-9 h-9 flex items-center justify-center">
                <img src={Folder} alt="" className="w-4 h-4" />
            </div>

            {/* Hover actions */}
            <div
                className="flex items-center overflow-hidden
                   max-w-0 group-hover:max-w-[80px]
                   transition-all duration-200"
            >
                {/* Divider */}
                <div className="h-4 w-px bg-gray-300 mx-1" />

                {/* Open */}
                <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    title="Open file"
                    className="w-8 h-8 flex items-center justify-center text-blue-500"
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M14 3h7v7" />
                        <path d="M10 14L21 3" />
                        <path d="M21 14v7h-7" />
                        <path d="M3 10v11h11" />
                    </svg>
                </a>

                {/* Delete */}
                <button
                    type="button"
                    onClick={onRemove}
                    title="Remove file"
                    className="w-8 h-8 flex items-center justify-center text-red-500"
                >
                    <img src={Delete} alt="" className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};



const formatTimestamp = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";

    const m = d.getMonth() + 1;
    const day = d.getDate();
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");

    return `${m}-${day}-${yy}, ${hh}:${mm}`;
};

/* ----------------------------- Types ----------------------------- */

type Props = {
    notes: AdminNote[];
    setNotes: (next: AdminNote[]) => void;
    isLoading?: boolean;
};

/* ----------------------------- Component ----------------------------- */

const AdminNotesPanelState: React.FC<Props> = ({
    notes = [],
    setNotes,
    isLoading = false,
}) => {
    /* ----------------------------- State ----------------------------- */
    const [isUploadingNoteAttachment, setIsUploadingNoteAttachment] = useState<number | null>(null);
    const [isUploadingNewNoteAttachment, setIsUploadingNewNoteAttachment] = useState(false);

    const [newNoteText, setNewNoteText] = useState("");
    const [editText, setEditText] = useState("");
    const [draftAttachments, setDraftAttachments] = useState<
        { name: string; url: string }[]
    >([]);
    const [pendingDraftFile, setPendingDraftFile] = useState<File | null>(null);


    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [pendingNoteUpload, setPendingNoteUpload] = useState<{
        noteIndex: number;
        file: File;
    } | null>(null);

    /* ----------------------------- Create ----------------------------- */

    const submitNewNote = () => {
        if (!newNoteText.trim()) return;

        const newNote: AdminNote = {
            text: newNoteText.trim(),
            createdAt: new Date().toISOString(),
            attachments: draftAttachments,
        };

        setNotes([newNote, ...notes]);
        setNewNoteText("");
        setDraftAttachments([]);
    };

    /* ----------------------------- Edit ----------------------------- */

    const startEdit = (index: number) => {
        setEditingIndex(index);
        setEditText(notes[index]?.text ?? "");
    };
    const saveEdit = () => {
        if (editingIndex === null) return;
        if (!editText.trim()) return;

        const next = [...notes];
        next[editingIndex] = {
            ...next[editingIndex],
            text: editText.trim(),
        };

        setNotes(next);
        setEditingIndex(null);
        setEditText("");
    };


    const cancelEdit = () => {
        setEditingIndex(null);
        setEditText("");
    };

    /* ----------------------------- Delete ----------------------------- */

    const removeNote = (index: number) => {
        setNotes(notes.filter((_, i) => i !== index));
        if (editingIndex === index) cancelEdit();
    };

    /* ----------------------------- Attachments ----------------------------- */

    const addAttachment = (index: number, file: File, url: string) => {
        const next = [...notes];
        const note = next[index];
        const attachments = note.attachments ?? [];

        next[index] = {
            ...note,
            attachments: [...attachments, { name: file.name, url }],
        };

        setNotes(next);
    };

    const removeAttachment = (noteIndex: number, attachmentIndex: number) => {
        const next = [...notes];
        const note = next[noteIndex];

        next[noteIndex] = {
            ...note,
            attachments: (note.attachments ?? []).filter(
                (_, i) => i !== attachmentIndex
            ),
        };

        setNotes(next);
    };

    /* ----------------------------- Render ----------------------------- */

    return (
        <div className="space-y-6">
            {/* ---------------- Add New Note ---------------- */}
            <div>
                <h3 className="text-[18px] font-medium mb-3">Add a New Note</h3>

                <textarea
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    placeholder="Write a note..."
                    className="w-full min-h-[80px] rounded-lg border border-gray-200 p-3 text-xs outline-none"
                />


                <div className="flex flex-wrap items-center justify-between mt-3 gap-3">
                    {/* Attachments */}
                    <div className="flex flex-wrap items-center gap-2">

                        <div className="flex items-center gap-3">
                            {draftAttachments.map((a, i) => (
                                <AttachmentIcon
                                    key={i}
                                    url={a.url}
                                    onRemove={() =>
                                        setDraftAttachments(
                                            draftAttachments.filter((_, idx) => idx !== i)
                                        )
                                    }
                                />
                            ))}

                            {/* Add attachment */}
                            {isUploadingNewNoteAttachment ? (
                                <div className="h-7 w-7 flex items-center justify-center">
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const input = document.createElement("input");
                                        input.type = "file";
                                        input.accept = "application/pdf,image/*";
                                        input.onchange = (e: any) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                setIsUploadingNewNoteAttachment(true);
                                                setPendingDraftFile(file);
                                            }
                                        };
                                        input.click();
                                    }}
                                    className="w-auto hover:bg-sky-50 h-auto flex items-center justify-center"
                                >
                                    <PlusIcon />
                                </button>
                            )}


                            {/* Hidden uploader */}
                            <div className="hidden">
                                <UploadField
                                    label=""
                                    value=""
                                    mode="file"
                                    initialFile={pendingDraftFile}
                                    forceUploadOnly
                                    onChange={(url: string) => {
                                        setDraftAttachments((prev) => {
                                            if (prev.some((a) => a.url === url)) return prev;
                                            return [...prev, { name: pendingDraftFile!.name, url }];
                                        });

                                        setPendingDraftFile(null);
                                        setIsUploadingNewNoteAttachment(false);
                                    }}

                                />
                            </div>



                        </div>


                    </div>

                    <LightCustomButton
                        text="Submit"
                        onClick={submitNewNote}
                        isActive={newNoteText.trim().length > 0}
                    />

                </div>
            </div>

            <hr />

            {/* ---------------- Past Notes ---------------- */}
            <div>
                <h3 className="text-[18px] font-medium mb-4">Past Notes</h3>

                {isLoading ? (
                    <div className="py-10 text-center text-sm text-gray-500">
                        Loading notes…
                    </div>
                ) : notes.length === 0 ? (
                    <div className="text-sm text-gray-500">No notes yet.</div>
                ) : (
                    <div className="space-y-4 max-h-[300px] overflow-y-auto pr-1">
                        {notes.map((note, i) => {
                            const isEditing = editingIndex === i;

                            return (
                                <div
                                    key={i}
                                    className="rounded-lg border-t border-border-subtle  p-4 space-y-3"
                                >
                                    {/* Text */}
                                    <div className="">
                                        {isEditing ? (
                                            <textarea
                                                value={editText}
                                                onChange={(e) => setEditText(e.target.value)}
                                                className="w-full min-h-[80px] rounded-lg border  p-3 text-xs outline-none"
                                            />
                                        ) : (
                                            <div className="relative bg-bg-base p-2 border rounded-lg border-border-table">
                                                <p className="text-xs font-light text-text-base whitespace-pre-wrap pb-4">
                                                    {note.text}
                                                </p>

                                                <span className="absolute bottom-1 right-2 text-[10px] text-gray-500">
                                                    {formatTimestamp(note.createdAt)}
                                                </span>
                                            </div>


                                        )}
                                    </div>


                                    {/* Attachments */}
                                    <div className="flex items-center justify-between pt-1  ">
                                        {/* LEFT: attachments */}
                                        <div className="flex flex-wrap items-center gap-2">
                                            {(note.attachments ?? []).map((a, ai) => (
                                                <AttachmentIcon
                                                    key={ai}
                                                    url={a.url}
                                                    onRemove={() => removeAttachment(i, ai)}
                                                />
                                            ))}

                                            {isUploadingNoteAttachment === i ? (
                                                <div className="h-7 w-7 flex items-center justify-center">
                                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const input = document.createElement("input");
                                                        input.type = "file";
                                                        input.accept = "application/pdf,image/*";
                                                        input.onchange = (e: any) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) {
                                                                setIsUploadingNoteAttachment(i);
                                                                setPendingNoteUpload({ noteIndex: i, file });
                                                            }
                                                        };
                                                        input.click();
                                                    }}
                                                    className="w-auto h-auto p-1 flex items-center justify-center"
                                                >
                                                    <PlusIcon />
                                                </button>
                                            )}


                                            <div className="hidden">
                                                <UploadField
                                                    label=""
                                                    value=""
                                                    mode="file"
                                                    initialFile={
                                                        pendingNoteUpload?.noteIndex === i
                                                            ? pendingNoteUpload.file
                                                            : null
                                                    }
                                                    forceUploadOnly
                                                    onChange={(url: string) => {
                                                        if (!pendingNoteUpload) return;

                                                        addAttachment(i, pendingNoteUpload.file, url);
                                                        setPendingNoteUpload(null);
                                                        setIsUploadingNoteAttachment(null);
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* RIGHT: edit / delete OR save / cancel */}
                                        <div className="flex items-center gap-3">
                                            {isEditing ? (
                                                <>
                                                    <button
                                                        onClick={cancelEdit}
                                                        className="text-sm text-gray-600"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={saveEdit}
                                                        className="text-sm font-medium text-blue-600"
                                                    >
                                                        Save
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => startEdit(i)}
                                                        className="text-sm border border-bg-button-secondary rounded-lg p-1"
                                                        title="Edit"
                                                    >
                                                        <img src={Edit} alt="" className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        onClick={() => removeNote(i)}
                                                        className="text-sm border border-bg-button-delete rounded-lg p-1"
                                                        title="Delete"
                                                    >
                                                        <img src={Delete} alt="" className="w-3 h-3" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <p className="text-[12px] text-gray-500">
                Admin notes and attachments are internal-only.
            </p>
        </div>
    );
};

export default AdminNotesPanelState;
